import { memo, useEffect, useState } from "react";
import { Paper, TextField, Box, Typography, Button, Switch, IconButton, Menu, MenuItem, Stack, Alert } from "@mui/material";
import { Add, Cancel, CloudUpload, MoreVert } from "@mui/icons-material";
import StarIcon from '../../assets/icons/star.png';
import RichTextEditor from "../../../components/rich-text/rich-text-editor";
import { Item } from "../../../../../models/assessment";
import ResourcesView from "../../../components/resources";

interface FileUploadViewProps {
    item: Item;
    setSaved: React.Dispatch<React.SetStateAction<boolean>>;
    register: (id: string, getItem: () => Item) => void;
    saveFunction: () => void;
}
  
function FileUploadView({ item, setSaved, register, saveFunction }: FileUploadViewProps) {
    const [localItem, setLocalItem] = useState(item);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [resourceAnchorEl, setResourceAnchorEl] = useState<null | HTMLElement>(null);
    const [resourcesVisible, setResourcesVisible] = useState<boolean>(false);
    const [descriptionVisible, setDescriptionVisible] = useState<boolean>(false);
    const [editing, setEditing] = useState<boolean>(false);

    
    useEffect(() => {
        register(item.id, () => localItem);
    }, [item.id, localItem, register]);

    const handleChange = (field: keyof Item, value: any) => {
        setSaved(false);
        setLocalItem((prev) => ({ ...prev, [field]: value }));
        saveFunction();
    };

    const toggleRequired = () => {
        handleChange("required", !localItem.required);
    };

     const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const toggleResourcesVisibility = () => {
        setResourcesVisible(!resourcesVisible);
        handleMenuClose();
    };

    const toggleDescriptionVisibility = () => {
        setDescriptionVisible(!descriptionVisible);
        handleMenuClose();
    };

    const handleImageClick = () => {
        setEditing(true);
    };

    const handleDoneClick = () => {
        setEditing(false);
    };

    return (
        <Paper 
            sx={{
                backgroundColor: '#fff',
                padding: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 2
            }}
        >
            {!editing ? (
                <>
                    <RichTextEditor
                        id={localItem.id}
                        type="title"
                        value={localItem.title}
                        onChange={(val) => handleChange("title", val)}
                        fullBorder={false}
                        userPlaceholder="Untitled File Upload Question"
                    />

                    {descriptionVisible && (
                        <RichTextEditor
                            id={localItem.id}
                            type="description"
                            value={localItem.description}
                            onChange={(val) => handleChange("description", val)}
                        />
                    )}

                    {resourcesVisible && (
                        <>
                            <Box
                                onClick={(event: React.MouseEvent<HTMLElement>) => setResourceAnchorEl(event.currentTarget)}
                                sx={{
                                    width: 50,
                                    height: 50,
                                    minWidth: 50,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    border: "1px dashed #ccc",
                                    flexShrink: 0,
                                    borderRadius: 2,
                                }}
                            >
                                <Add sx={{ fontSize: 24, color: "#757575" }} />
                            </Box>

                            <Stack direction="row" spacing={1} alignItems="center" sx={{ overflowX: "auto", width: "100%", pb: 1 }}>
                                {localItem.resources.map((res, i) => {
                                    return (
                                        <Paper
                                            key={`${res}-resource-${i}`}
                                            sx={{
                                            width: 50,
                                            height: 50,
                                            minWidth: 50,
                                            overflow: "hidden",
                                            position: "relative",
                                            borderRadius: 1,
                                            backgroundColor: "#000",
                                            flexShrink: 0,
                                            }}
                                        >
                                            <img
                                                src={res}
                                                alt={`resource-${i}`}
                                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                            />

                                            <IconButton
                                                size="small"
                                                sx={{
                                                    position: "absolute",
                                                    top: 1,
                                                    right: 0.5,
                                                    backgroundColor: "rgba(0,0,0,0.3)",
                                                    color: "#fff",
                                                    padding: "2px",
                                                    "&:hover": {
                                                    backgroundColor: "rgba(0,0,0,0.5)",
                                                    },
                                                }}
                                                onClick={() => {
                                                    handleChange("resources", localItem.resources.filter((r) => r !== res));
                                                }}
                                            >
                                                <Cancel fontSize="small" />
                                            </IconButton>
                                        </Paper>
                                    );
                                })}
                            </Stack>

                            <ResourcesView
                                id={localItem.id}
                                resourceAnchorEl={resourceAnchorEl}
                                setResourceAnchorEl={setResourceAnchorEl}
                                onselect={(res: string) => handleChange("resources", [...localItem.resources, res])}
                            />
                        </>
                    )}

                    <Box
                        sx={{
                            border: '1px dashed #ccc',
                            borderRadius: 1,
                            padding: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f9f9f9',
                            marginBottom: 2,
                            cursor: 'not-allowed'
                        }}
                    >
                        <CloudUpload sx={{ fontSize: 20, color: '#999', mb: 1, display: 'flex' }} />
                        
                        <Button 
                            variant="outlined" 
                            disabled 
                            sx={{ mt: 1 }}
                        >
                            Browse Files
                        </Button>
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                            Maximum file size: 50MB
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <Box sx={{ width: 24, height: 24 }}>
                            <img
                                src={StarIcon}
                                alt="AutoMarker"
                                style={{ width: 24, height: 24, cursor: 'pointer' }}
                                onClick={handleImageClick}
                            />
                        </Box>
                        
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography variant="body2">Required</Typography>
                            <Switch checked={!!localItem.required} onChange={toggleRequired} />

                            <IconButton onClick={handleMenuOpen} sx={{ marginLeft: 1 }}>
                                <MoreVert />
                            </IconButton>

                            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                                <MenuItem onClick={toggleResourcesVisibility}>
                                    {resourcesVisible ? "Hide resources" : "Show resources"}
                                </MenuItem>
                                <MenuItem onClick={toggleDescriptionVisibility}>
                                    {descriptionVisible ? "Hide description" : "Show description"}
                                </MenuItem>
                            </Menu>
                        </Box>
                    </Box>
                </>
            ) : (
                <>
                    <TextField
                        variant="standard"
                        value={localItem.marks}
                        onChange={(e) => handleChange("marks", parseInt(e.target.value))}
                        label="Marks"
                        type="number"
                        sx={{ maxWidth: "50%" }}
                    />

                    <Alert severity="info">
                        Auto Marker is not supported for files yet.
                    </Alert>

                    <Button
                        variant="contained"
                        onClick={handleDoneClick}
                        sx={{
                            alignSelf: "flex-end",
                            marginTop: 2,
                            paddingLeft: 6,
                            paddingRight: 6,
                            backgroundColor: "#53826b",
                        }}
                    >
                        Done
                    </Button>
                </>
            )}
           
                
        </Paper>
    );
}

export default memo(FileUploadView);